import {
  DocketItemStatus,
  DocketStatus,
  GroupDisplayStyle,
  DocketItem,
  NotifierDocketItems,
  NotifierDisplayStyle,
  OrderTypeCode,
} from '@oolio-group/domain';
import { CourseStatus } from './constants';

export interface SegmentControlTabs {
  label: string;
  value: string | DocketStatus | CourseStatus;
  badge?: string | number;
}

export const DEFAULT_DOCKET_NAME = 'Guest';

export enum DocketDisplayItemType {
  DOCKET_TITLE_BAR = 'DOCKET_TITLE_BAR',
  TRAINING_DOCKET = 'TRAINING_DOCKET',
  DOCKET_HEADER = 'DOCKET_HEADER',
  ORDER_NOTE = 'ORDER_NOTE',
  ITEM = 'ITEM',
  COURSE = 'COURSE',
  CONTINUE = 'CONTINUE',
  CONTINUED = 'CONTINUED',
  RECALL_BUTTON = 'RECALL_BUTTON',
  VOID = 'VOID',
  RECALLED_TAG = 'RECALLED_TAG',
  DUE_AT = 'DUE_AT',
  TRANSFERRED_TO_LABEL = 'TRANSFERRED_TO_LABEL',
  TRANSFERRED_FROM_LABEL = 'TRANSFERRED_FROM_LABEL',
  OTHER_ITEMS = 'OTHER_ITEMS',
  SEAT_NUMBER = 'SEAT_NUMBER',
  CATEGORY = 'CATEGORY',
}

export interface FulfilmentDetails {
  catering?: {
    type: OrderTypeCode;
  };
}

export interface DocketDisplayOrder {
  id: string;
  type: DocketDisplayItemType;
  name: string;
  orderNumber: string;
  orderType: string;
  time: string;
  height: number;
  section: string;
  table: string;
  customer: string;
  salesChannel: string;
  isLastItemToBump: boolean;
  status: DocketStatus;
  source: string;
  isTraining?: boolean;
  tags?: string[];
  notifierItems?: NotifierDocketItems[];
  rushedAt?: number;
  fulfilmentDetails?: FulfilmentDetails;
}

interface Position {
  rowPosition?: number;
  parentRowPosition?: number;
  needsFullColumn?: boolean;
  pageIndicator?: string;
}

export interface DisplayPreference {
  displayStyle: GroupDisplayStyle;
}

export interface CourseCallInfo {
  isCalled: boolean;
  calledAt: number;
  calledBy: string;
  calledByName?: string;
}

export interface DocketDisplayBaseItem {
  type: DocketDisplayItemType;
  name: string;
  height: number;
  orderNumber?: string;
  position?: Position;
  tags?: string[];
  primaryGrouping?: boolean;
  displayPreference?: DisplayPreference;
  notifierItems?: NotifierDocketItems[];
  rushedAt?: number;
  courseCallInfo?: CourseCallInfo;
  areAllItemsBumped?: boolean;
  orderTypeCode?: OrderTypeCode;
  docketCount?: string;
  orderId?: string;
}

export interface DocketDisplayModifierItem {
  name: string;
  removeDefault?: boolean;
}

export interface DocketDisplayComboItem {
  name: string;
  notes?: string;
  modifiers?: DocketDisplayModifierItem[];
}

export interface DocketDisplayOrderItem extends DocketDisplayBaseItem {
  quantity: number;
  docketId: string;
  id: string;
  note?: string;
  modifiers?: DocketDisplayModifierItem[];
  comboItems?: DocketDisplayComboItem[];
  variant?: string;
  itemStatus: DocketItemStatus;
  numberOfLinesForNotes?: number;
  numberOfLinesForProduct: number;
  orderItemId: string;
  orderType?: string;
  salesChannel?: string;
  seatName?: string;
  consolidatedOrderItemIds?: string[];
  course?: {
    id?: string;
    name?: string;
    priority?: number;
    callInfo?: CourseCallInfo;
  };
}

export interface AllDayViewItem {
  product?: string;
  variation?: string;
  course?: string;
  quantity?: number;
  modifiers?: string[];
  docketItemIds?: string[];
  orderItemIds?: string[];
  groupKey: string;
}

export interface DocketDisplayContinueItem extends DocketDisplayBaseItem {
  docketId: string;
  status: DocketStatus;
}

export interface DocketDisplayContinuedItem extends DocketDisplayBaseItem {
  docketId: string;
  orderNumber: string;
  status: DocketStatus;
}

export type DocketDisplayItem =
  | DocketDisplayOrderItem
  | DocketDisplayBaseItem
  | DocketDisplayContinueItem
  | DocketDisplayContinuedItem;

export type DocketDisplayOtherItem = {
  type: DocketDisplayItemType;
  name: string;
  height: number;
  items: DocketItem[];
  notifierItems: NotifierDocketItems[];
  docketId: string;
  orderNumber: string;
  status: DocketStatus;
  printerProfileId: string;
  orderTypeId: string;
};

export enum DocketSource {
  API = 'API',
  SUBSCRIPTION = 'SUBSCRIPTION',
  POOLING = 'POOLING',
}

export type DocketUpdate = {
  timestamp: number;
  source?: DocketSource;
  triggerSound?: boolean;
};

export type PageDimension = {
  startHeight: number;
  endHeight: number;
};

export type DocketHeaderColor = {
  [key: string]: string;
};

export type Nav = {
  navigate: (value: string) => void;
};

export enum Scale {
  SMALL = 'SMALL',
  MEDIUM = 'MEDIUM',
  LARGE = 'LARGE',
}

export enum Theme {
  LIGHT = 'light',
  DARK = 'dark',
}

export interface StatsBarUpdate {
  lastUpdatedAt: number;
}

export type DocketDetailsProps = {
  orderTypeId: string;
  docketId: string;
  orderNumber: string;
  items: DocketItem[];
  status: string;
  notifierItems?: NotifierDocketItems[];
  displayStyle?: NotifierDisplayStyle;
};
